import { useEffect, useRef } from "react";
import ParticleCanvas from "./components/ParticleCanvas";
import Header from "./components/Header";
import HeroBanner from "./components/HeroBanner";
import TableOfContents from "./components/TableOfContents";
import WhatMakesSpecial from "./components/WhatMakesSpecial";
import SecuritySection from "./components/SecuritySection";

import GamesSection from "./components/GamesSection";
import SportsSection from "./components/SportsSection";
import BonusesSection from "./components/BonusesSection";
import PaymentSection from "./components/PaymentSection";
import MobileSection from "./components/MobileSection";
import CustomerSection from "./components/CustomerSection";
import ConclusionFAQ from "./components/ConclusionFAQ";
import Footer from "./components/Footer";
import SEOBlogSection from "./components/SEOBlogSection";

export default function Home() {
  const mainRef = useRef<HTMLDivElement>(null);

  // Parallax mouse effect on background orbs
  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xPct = (clientX / innerWidth - 0.5) * 2;
      const yPct = (clientY / innerHeight - 0.5) * 2;
      document.documentElement.style.setProperty("--mouse-x", `${xPct}`);
      document.documentElement.style.setProperty("--mouse-y", `${yPct}`);
    };
    window.addEventListener("mousemove", handleMouse);
    return () => window.removeEventListener("mousemove", handleMouse);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#030721", color: "white" }}>
      {/* Particle layer */}
      <ParticleCanvas />

      {/* Global ambient background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Deep blue orb top-left */}
        <div
          className="orb w-[800px] h-[800px] animate-pulse-glow"
          style={{
            top: "-300px",
            left: "-300px",
            background: "radial-gradient(circle, rgba(26,58,143,0.18) 0%, transparent 65%)",
          }}
        ></div>
        {/* Purple orb top-right */}
        <div
          className="orb w-[700px] h-[700px] animate-pulse-glow"
          style={{
            top: "-200px",
            right: "-250px",
            background: "radial-gradient(circle, rgba(107,33,168,0.15) 0%, transparent 65%)",
            animationDelay: "2s",
          }}
        ></div>
        {/* Gold orb center */}
        <div
          className="orb w-[500px] h-[500px] animate-pulse-glow"
          style={{
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "radial-gradient(circle, rgba(201,168,76,0.05) 0%, transparent 65%)",
            animationDelay: "4s",
          }}
        ></div>
        {/* Bottom blue orb */}
        <div
          className="orb w-[600px] h-[600px] animate-pulse-glow"
          style={{
            bottom: "-200px",
            right: "-100px",
            background: "radial-gradient(circle, rgba(26,58,143,0.12) 0%, transparent 65%)",
            animationDelay: "1s",
          }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10" ref={mainRef}>
        <Header />
        <HeroBanner />

        {/* Section divider */}
        <div className="max-w-5xl mx-auto px-6">
          <div className="light-line"></div>
        </div>

        <div className="max-w-5xl mx-auto px-6">
          <TableOfContents />
        </div>

        <WhatMakesSpecial />

        <SecuritySection />

        <div className="max-w-5xl mx-auto px-6">
          <div className="light-line opacity-40"></div>
        </div>

        <GamesSection />

        <div className="max-w-5xl mx-auto px-6">
          <div className="light-line opacity-40"></div>
        </div>

        <SportsSection />

        <div className="max-w-5xl mx-auto px-6">
          <div className="light-line opacity-40"></div>
        </div>

        <BonusesSection />

        <div className="max-w-5xl mx-auto px-6">
          <div className="light-line opacity-40"></div>
        </div>

        <PaymentSection />

        <div className="max-w-5xl mx-auto px-6">
          <div className="light-line opacity-40"></div>
        </div>

        <MobileSection />

        <div className="max-w-5xl mx-auto px-6">
          <div className="light-line opacity-40"></div>
        </div>

        <CustomerSection />

        <div className="max-w-5xl mx-auto px-6">
          <div className="light-line opacity-40"></div>
        </div>

        <div className="max-w-5xl mx-auto px-6">
          <div className="light-line opacity-40"></div>
        </div>

        <SEOBlogSection />

        <ConclusionFAQ />

        <Footer />
      </div>

      {/* Scroll to top */}
      <ScrollTopButton />
    </div>
  );
}

function ScrollTopButton() {
  const handleClick = () => window.scrollTo({ top: 0, behavior: "smooth" });
  return (
    <button
      onClick={handleClick}
      className="fixed bottom-8 right-8 z-50 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-all duration-300 hover:scale-110"
      style={{
        background: "linear-gradient(135deg, rgba(26,58,143,0.6), rgba(107,33,168,0.6))",
        border: "1px solid rgba(201,168,76,0.3)",
        boxShadow: "0 0 16px rgba(201,168,76,0.15)",
      }}
      aria-label="Scroll to top"
    >
      <i className="ri-arrow-up-line text-[#c9a84c] text-sm"></i>
    </button>
  );
}
